import { CountryList, states } from '../jsons';

/**
 * US address formatting per USPS Publication 28:
 *
 *   line1: 123 Main St Apt 4B          (delivery line — street + secondary unit, §213)
 *   line2: Los Angeles, CA 90001-1234  (city, 2-letter state, ZIP / ZIP+4, §224 + App. B)
 *   full:  123 Main St Apt 4B, Los Angeles, CA 90001-1234
 *
 * Pub 28's mailpiece standard is ALL CAPS with no punctuation (§222 makes punctuation optional).
 * This is for screens, so it keeps mixed case and the comma after the city (Google's formatted_address
 * convention). Country is never shown. Falls back to `formattedAddress` for legacy docs with no
 * structured fields.
 */

const US_COUNTRY_NAMES = ['us', 'usa', 'u.s.', 'u.s.a.', 'united states', 'united states of america'];

// USPS Pub 28 Appendix C2 secondary unit designators → approved abbreviation (title-cased for screen display).
const UNIT_DESIGNATORS = {
    apartment: 'Apt',
    apt: 'Apt',
    basement: 'Bsmt',
    bsmt: 'Bsmt',
    building: 'Bldg',
    bldg: 'Bldg',
    department: 'Dept',
    dept: 'Dept',
    floor: 'Fl',
    fl: 'Fl',
    front: 'Frnt',
    frnt: 'Frnt',
    hanger: 'Hngr',
    hngr: 'Hngr',
    key: 'Key',
    lobby: 'Lbby',
    lbby: 'Lbby',
    lot: 'Lot',
    lower: 'Lowr',
    lowr: 'Lowr',
    office: 'Ofc',
    ofc: 'Ofc',
    penthouse: 'Ph',
    ph: 'Ph',
    pier: 'Pier',
    rear: 'Rear',
    room: 'Rm',
    rm: 'Rm',
    side: 'Side',
    slip: 'Slip',
    space: 'Spc',
    spc: 'Spc',
    stop: 'Stop',
    suite: 'Ste',
    ste: 'Ste',
    trailer: 'Trlr',
    trlr: 'Trlr',
    unit: 'Unit',
    upper: 'Uppr',
    uppr: 'Uppr',
};

const STATE_CODE_BY_NAME = states.reduce((acc, { name, id }) => {
    acc[name.toLowerCase()] = id;
    return acc;
}, {});

const clean = (value) => (typeof value === 'string' ? value.trim().replace(/\s+/g, ' ') : '');

const formatState = (state) => {
    if (!state) return '';
    if (/^[a-z]{2}$/i.test(state)) return state.toUpperCase();
    return STATE_CODE_BY_NAME[state.toLowerCase()] || state;
};

const formatZip = (zip) => {
    const digits = zip.replace(/\D/g, '');
    if (digits.length === 9) return `${digits.slice(0, 5)}-${digits.slice(5)}`;
    return zip;
};

// "Suite 200" → "Ste 200", "apt. 4b" → "Apt 4B", "#12" / "12" → "# 12".
// Pub 28 §213: "#" only when the designator is unknown, with a space before the number.
const formatUnit = (unit) => {
    if (!unit) return '';
    const match = unit.match(/^([a-z]+)\.?\s*(.*)$/i);
    const designator = match && UNIT_DESIGNATORS[match[1].toLowerCase()];
    if (designator) return [designator, match[2].toUpperCase()].filter(Boolean).join(' ');
    return `# ${unit.replace(/^#\s*/, '').toUpperCase()}`;
};

// Legacy manual-entry docs were saved as "street,  city,  state,  zip,  country" (see addressInput.js).
const cleanFormattedAddress = (formatted) =>
    formatted
        .split(',')
        .map(clean)
        .filter(Boolean)
        .filter((part, i, parts) => !(i === parts.length - 1 && US_COUNTRY_NAMES.includes(part.toLowerCase())))
        .join(', ');

const ZIP_RE = /^\d{5}(-?\d{4})?$/;
const TRAILING_ZIP_RE = /\s+\d{5}(-?\d{4})?$/;
const US_STATE_CODES = new Set(states.map(({ id }) => id));
const COUNTRY_NAMES = new Set(CountryList.map(({ name }) => name.toLowerCase()));
const hasDigit = (s) => /\d/.test(s);
// "N.Y." / "n y" → "NY" when that's a US code; anything else untouched.
const asStateCode = (s) => {
    const squeezed = s.replace(/[.\s]/g, '').toUpperCase();
    return squeezed.length === 2 && US_STATE_CODES.has(squeezed) ? squeezed : formatState(s);
};
const isUSState = (s) => US_STATE_CODES.has(asStateCode(s));

/**
 * `{ city, state }` parsed from a formatted address line. Handles Google's "street, City, ST 12345,
 * USA", the manual-entry "street,  city,  state,  zip,  country", and "street, City ST 12345" with no
 * comma before the state. A part containing digits is a street/unit, never a city or state.
 */
const parsePlace = (formatted) => {
    const parts = cleanFormattedAddress(clean(formatted)).split(', ').filter(Boolean);
    // Drop trailing zip-only parts ("…, CA, 90001") and a non-US country ("…, Shirak Province, Armenia").
    while (parts.length && (ZIP_RE.test(parts[parts.length - 1]) || COUNTRY_NAMES.has(parts[parts.length - 1].toLowerCase()))) parts.pop();
    if (!parts.length) return { city: '', state: '' };

    const last = parts[parts.length - 1].replace(TRAILING_ZIP_RE, '').trim();
    // "Los Angeles CA" in one part — split off a trailing US state code.
    const glued = last.match(/^(.*\D)\s+([A-Za-z.]{2,4})$/);
    if (glued && isUSState(glued[2]) && !hasDigit(glued[1])) return { city: glued[1].trim(), state: asStateCode(glued[2]) };

    if (parts.length < 2 || hasDigit(last)) return { city: '', state: '' };
    const city = parts[parts.length - 2];
    return { city: hasDigit(city) ? '' : city, state: asStateCode(last) };
};

/**
 * `{ city, state }` of an address, with state as the 2-letter code ("California", "ca" and "C.A."
 * are one place). Prefers the structured fields; falls back per field to parsing `formattedAddress`
 * (legacy docs and the map pins carry only that). Unknown parts come back as ''.
 */
export const addressPlace = (address) => {
    if (!address) return { city: '', state: '' };
    let city = clean(address.city);
    let state = clean(address.state) ? asStateCode(clean(address.state)) : '';
    if (!city || !state) {
        const parsed = parsePlace(address.formattedAddress);
        city = city || parsed.city;
        state = state || parsed.state;
    }
    return { city, state };
};

/**
 * True when an address says it is in the US: its state resolves to a US code, or its formatted line
 * ends with a US country name. `place` can be passed when the caller already has `addressPlace(address)`.
 */
export const isUSAddress = (address, place = addressPlace(address)) => {
    if (US_STATE_CODES.has(place?.state)) return true;
    const parts = clean(address?.formattedAddress).split(',').map(clean).filter(Boolean);
    return parts.length > 0 && US_COUNTRY_NAMES.includes(parts[parts.length - 1].toLowerCase());
};

export const formatAddress = (address) => {
    const empty = { line1: '', line2: '', full: '' };
    if (!address) return empty;

    const street = clean(address.street);
    const unit = formatUnit(clean(address.unitNumber));
    const city = clean(address.city);
    const state = formatState(clean(address.state));
    const zip = formatZip(clean(address.zip));

    if (!street && !city && !state && !zip) {
        const fallback = cleanFormattedAddress(clean(address.formattedAddress));
        return { line1: fallback, line2: '', full: fallback };
    }

    const line1 = [street, unit].filter(Boolean).join(' ');
    const stateZip = [state, zip].filter(Boolean).join(' ');
    const locality = [city, stateZip].filter(Boolean).join(', ');

    return { line1, line2: locality, full: [line1, locality].filter(Boolean).join(', ') };
};
